/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, Component, EventEmitter, Injector, Output, TemplateRef, ViewChild } from '@angular/core';
import { ActionAbstract } from '@dm/core';

import { type Eula } from './eula';
import { IsSignedEulaE } from './eula.enum';
import { EulaService } from './eula.service';
import { EulaStrService } from './eula-str.service';

@Component({
    selector: 'app-eula',
    templateUrl: './eula.component.html',
    styleUrls: ['./eula.component.scss'],
})
export class EulaComponent extends ActionAbstract implements AfterViewInit {
    /** 通知登录页显示签署EULA入口按钮 */
    @Output() setLoginSignEulaEntryEmit = new EventEmitter<boolean>();
    /** 通知登录页记录签署的EULA版本 */
    @Output() setSignEulaVersionEmit = new EventEmitter<string>();
    /** 签署EULA弹框底部确认取消按钮 */
    @ViewChild('signEulaConfirmFooter') signEulaConfirmFooter: TemplateRef<any>;
    /** 签署EULA同意标识 */
    isSignedEula = this.eulaService.getIsSignedEula();
    i18n = this.eulaStrService.i18n;
    isSignEulaNeeded = this.eulaService.getIsSignEulaNeeded();

    constructor(
        private eulaStrService: EulaStrService,
        private eulaService: EulaService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngAfterViewInit(): void {
        this.signEula();
    }

    /** 弹出签署条款弹框 */
    signEula(): void {
        // 弹框加载使用lazyLoad，service初始化时有10毫秒延迟，当刷新时，浏览器最小化或者切换到其他页签，定时器不准时，导致未按正常时序加载，故增加时延执行
        setTimeout(() => {
            this.promptService.normal({
                id: 'sign_eula_info_prompt',
                className: this.isSignEulaNeeded ? 'sign_eula_info_prompt' : 'show_eula_info_prompt',
                title: this.i18n.signEulaAgreementTitle,
                content: this.i18n.signEulaAgreementContent,
                width: '70%',
                height: '80%',
                footer: this.isSignEulaNeeded ? this.signEulaConfirmFooter : undefined,
            });
        }, 100);
    }

    /**
     * 勾选确认后，通知登录页隐藏签署入口及缓存需下发给接口的版本信息
     */
    signEulaConfirmOk(): void {
        if (window.location.pathname !== '/login') {
            const params: Eula = {
                isSignedEula: IsSignedEulaE.YES,
                eulaVersion: this.eulaStrService.i18n.eulaVersion,
            };
            this.eulaService.signEula(params).subscribe(() => {
                // 重新查询EULA信息
                this.eulaService.checkEulaInfo().subscribe();
                this.promptService.close();
            });
        } else {
            this.setLoginSignEulaEntryEmit.emit(true);
            this.setSignEulaVersionEmit.emit(this.i18n.eulaVersion);
            this.promptService.close();
        }
    }

    /**
     * 未确认勾选，通知登录页显示签署入口及清除版本信息
     */
    signEulaConfirmCancel(): void {
        this.setLoginSignEulaEntryEmit.emit(false);
        this.setSignEulaVersionEmit.emit(undefined);
        this.promptService.close();
    }
}
